// International device orders — the International Requests tab. Mirrors the staff-api IntlRequest*
// GraphQL shapes (apps/api/src/app/intl-requests/intl-requests.response.ts and .inputs.ts).
// Vendor by copy.
//
// There is no codegen in this repo, so these are hand-written mirrors and `pnpm check:schema` is
// what catches an operation selecting a field the schema does not have. Run it after editing
// either side.

/** Mirrors the CHECK constraint in migration 031. No UNKNOWN — a rep picks this from a list. */
export const INTL_DEVICE_MODELS = ['OPTISTICK', 'PRO', 'PROMAX'] as const;
export type IntlDeviceModel = (typeof INTL_DEVICE_MODELS)[number];

export const INTL_DEVICE_LABELS: Readonly<Record<IntlDeviceModel, string>> = {
  OPTISTICK: 'OptiStick',
  PRO: 'Pro Player',
  PROMAX: 'ProMax Player',
};

/**
 * The DERIVED stage. There is no `status` column — see migration 031's enforcement note 1 and the
 * rule 019:28-31 states: a stored status is a second source of truth that drifts from the stamps
 * beside it.
 *
 * IN THE ORDER A REQUEST TRAVELS THROUGH THEM (requester, 2026-09-10). The last two are the
 * terminal states and they FORK ON PAYMENT:
 *
 *   FULFILLED         Amazon released it and there is a tracking number — and it is paid for.
 *   FULFILLED_UNPAID  the same shipment, with the invoice still outstanding.
 *
 * ⚠️ THAT FORK PUTS PAYMENT BACK ON THIS AXIS, which is the thing removing `PAID` took off it.
 * It is a deliberate exception rather than a slip: the two terminal states are operationally
 * different in a way no other pair is — one is finished, the other is money owed on hardware
 * already in a customer's hands, which is the set somebody chases and eventually switches an
 * account off over. Being able to see that in the stage column is worth the exception. Payment
 * still does NOT fork any earlier stage, and `invoiceStatus` remains the full picture.
 *
 * ⚠️ `QUOTED` / `QUOTE_EXPIRED` SIT BEFORE THE INVOICE STAGES HERE AND ARE DERIVED AFTER THEM.
 * This array is reading order — what a rep sees the request pass through — while the server
 * resolves the stage latest-fact-first, so a converted request reads `INVOICE_DRAFT` even though
 * it still carries the quote it came from.
 *
 * ⚠️ A CANCELLED QUOTE HAS ITS OWN STAGE, reversing this feature's first version where it read
 * `FILED`. That was true about what is OUTSTANDING and false about what HAPPENED — `FILED` hid
 * that a quote had ever been raised and withdrawn.
 *
 * The quote step is OPTIONAL. "Filed → invoice" remains a complete journey, and most requests
 * that do not need a customer sign-off take it.
 */
export const INTL_STAGES = [
  'FILED',
  'QUOTED',
  'QUOTE_EXPIRED',
  'QUOTE_CANCELLED',
  'INVOICE_DRAFT',
  'INVOICED',
  'ORDER_CREATED',
  'ORDER_CANCELLED',
  'FULFILLED',
  'FULFILLED_UNPAID',
] as const;
export type IntlStage = (typeof INTL_STAGES)[number];

/** Labels exactly as the requester wrote them (2026-09-09). */
export const INTL_STAGE_LABELS: Readonly<Record<IntlStage, string>> = {
  FILED: 'Filed',
  QUOTED: 'Quoted',
  // "Quote expired", not a bare "Expired": this badge shares the Stage column with FILED and
  // INVOICED, so the noun is what says WHICH thing lapsed. A request whose quote expired has not
  // itself expired — it is still perfectly billable, by re-quoting or by invoicing directly.
  QUOTE_EXPIRED: 'Quote expired',
  // "canceled", the American spelling, DELIBERATELY — and note the sibling below reads
  // "Fulfilment cancelled". This stage is about the STRIPE QUOTE, whose own status vocabulary is
  // `canceled`, and this repo's rule is to carry Stripe's word rather than re-spell it. The
  // fulfilment one is about an Amazon order and is ours to name, so it stays British.
  QUOTE_CANCELLED: 'Quote canceled',
  // Hyphens dropped 2026-09-10 (requester). They read as compound nouns — "invoice-draft" as a
  // kind of thing — where these are plainly two words describing a state.
  INVOICE_DRAFT: 'Invoice draft',
  INVOICED: 'Invoiced',
  ORDER_CREATED: 'Order created',
  // A cancelled fulfilment order KEEPS its `mcf_order_id` — Amazon never releases an id, so a
  // retry has to use a new one — which meant a cancelled request read as "Order created", the
  // opposite of what happened (requester, 2026-09-17, on IR-104 in production).
  ORDER_CANCELLED: 'Fulfilment cancelled',
  FULFILLED: 'Fulfilled',
  FULFILLED_UNPAID: 'Fulfilled unpaid',
};

/**
 * STRIPE's own invoice statuses, verbatim — the SECOND axis, beside the stage.
 *
 * Never re-worded: the tab and the Stripe dashboard must not disagree, and nobody should have to
 * learn a translation. `none` is ours, for a row with no invoice at all.
 */
export const STRIPE_INVOICE_STATUSES = ['draft', 'open', 'paid', 'void', 'uncollectible'] as const;
export type StripeInvoiceStatus = (typeof STRIPE_INVOICE_STATUSES)[number];

/**
 * How each Stripe status should READ to a person, and how loudly.
 *
 * `uncollectible` is the one worth naming carefully: it means written off, NOT overdue, and an
 * earlier version of the device-order code conflated the two and asserted something Stripe never
 * said.
 */
export const STRIPE_INVOICE_STATUS_TONE: Readonly<
  Record<StripeInvoiceStatus, 'neutral' | 'good' | 'warn' | 'bad'>
> = {
  draft: 'neutral',
  open: 'warn',
  paid: 'good',
  void: 'neutral',
  uncollectible: 'bad',
};

/** Mirrors the service's own bounds, which mirror the CHECK constraints in migration 031. */
export const INTL_REQUEST_MAX_QUANTITY = 9999;
export const INTL_REQUEST_MIN_NOTE_LEN = 3;
export const INTL_REQUEST_MAX_NOTE_LEN = 1000;

/** A Stripe customer id, as the intake form validates it before sending. */
export const STRIPE_CUSTOMER_ID_RE = /^cus_[A-Za-z0-9]+$/;

/**
 * ISO 3166-1 alpha-2, officially assigned — what the country picker offers.
 *
 * VENDOR-BY-COPY. The twin is `ISO_ALPHA2` in
 * apps/api/src/app/intl-requests/intl-requests.service.ts, which the server validates against.
 * apps/api deliberately does not depend on this package (see rma.service.ts and
 * opticlock.format.ts for the same arrangement and the reason), so this is a second copy on
 * purpose — and BOTH copies are asserted against the same expected count and spot-checks in their
 * own tests. That is the drift detector: change one list and the other's spec goes red.
 *
 * Codes only, no labels: `Intl.DisplayNames` renders the names at display time, so we do not
 * carry 249 English strings that would drift. Same choice as the Quote Generator's countries.ts.
 */
export const ISO_ALPHA2: readonly string[] = (
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO ' +
  'BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ ' +
  'DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP ' +
  'GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG ' +
  'KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML ' +
  'MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE ' +
  'PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL ' +
  'SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM ' +
  'US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'
).split(' ');

/**
 * ── WHERE THIS FEATURE CAN SHIP ────────────────────────────────────────────────────────────────
 *
 * The destinations Amazon MCF can fulfil for us, and therefore the ONLY countries a request may
 * be filed to (requester, 2026-09-09: block it at intake).
 *
 * WHY INTAKE AND NOT ONLY FULFILMENT. Until now a rep could file to any of the 249 ISO countries,
 * we would draft the invoice, the customer would PAY, and fulfilment would then discover Amazon
 * cannot ship there — money taken with no way to deliver. That was survivable while the servable
 * set was "most of Europe plus the US"; it is not once the set is a short list. So the check
 * moved to the front of the flow, where it costs a rep ten seconds instead of a refund.
 *
 * A CAPABILITY LIST, NOT A POLICY ONE (requester, 2026-09-09). These are the countries Amazon
 * *can* serve, not a subset we choose to serve — which is why the refusal says Amazon cannot ship
 * there rather than that we decline to. If that ever inverts, the message must change with it:
 * "we do not serve X" and "nobody can ship to X" send the reader to different people.
 *
 * VENDORED BY COPY from `mcf-routing.ts`'s `MCF_MARKETPLACE_CANDIDATES`, which is the api-side
 * authority and also records which marketplace each country ships FROM. `apps/api` does not
 * depend on this package, so the two are kept in step by a drift assertion on the count — the
 * same arrangement `ISO_ALPHA2` already has, for the same reason: one list, two readers, and a
 * test that fails when only one of them is edited.
 */
export const MCF_SERVABLE_COUNTRIES: readonly string[] = [
  // The two non-EU home markets.
  'US',
  'GB',
  // The 27 destinations Amazon DE MCF delivers to, per the requester's own list (2026-09-14).
  //
  // ⚠️ IT EQUALS THE EU-27 TODAY AND DID NOT FOUR DAYS AGO. HR was taken off on 2026-09-10
  // against a 26-country list and put back on 2026-09-14 against a 27-country one — both times
  // the requester's list, never an inference from membership. So this stays a spelled-out
  // coverage list rather than an `isEuMember()` call: the set has already proved it can move
  // while the EU does not, in both directions, and each direction has its own cost. A country
  // missing here is refused at intake for no reason; one here that Amazon will not ship to lets
  // a rep file a request Amazon refuses AFTER the customer has paid.
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'HU', 'IE',
  'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK',
];

/**
 * ── THE FOUR "REMOTE" EU DESTINATIONS ARE NO LONGER A CONCEPT ─────────────────────────────────
 *
 * Finland, Greece, Cyprus and Malta. `INTL_REMOTE_COUNTRIES` and `isIntlRemoteCountry` lived
 * here and are DELETED (requester, 2026-09-22). Their whole history in three steps:
 *
 *   2026-09-16  their own freight tier, 40 + 30 against 30 + 20 — the customer paid more
 *   2026-09-20  the tier collapsed into one 50 + 20 for every destination; the four became a
 *               FULFILMENT hint only, a red note suggesting China Fulfilled over Amazon MCF
 *   2026-09-22  the tiers are gone entirely, and so is the note
 *
 * ⚠️ DO NOT REINTRODUCE THE LIST WITHOUT A NEW REASON. Freight is now Amazon's measured MCF fee
 * for the actual shipment, so there is no tier for a destination to sit in and no surcharge to
 * apply — and the drawer shows what Amazon quoted per speed, so "is this one dear?" is answered
 * by a figure rather than by a standing warning about a country. If Amazon does charge more into
 * these four, the panel already says so, on the order in front of you.
 *
 * The api-side twin in `mcf-routing.ts` went with it, along with the drift assertion that kept
 * the two counts in step — there is nothing left to drift.
 */

/** The drift detector's number. Its twin is `MCF_SERVABLE_COUNT` in `mcf-routing.ts`. */
export const MCF_SERVABLE_COUNT = MCF_SERVABLE_COUNTRIES.length;

const MCF_SERVABLE_SET: ReadonlySet<string> = new Set(MCF_SERVABLE_COUNTRIES);

/** May a request be filed to this country at all? */
export function isMcfServable(country: string | null | undefined): boolean {
  return MCF_SERVABLE_SET.has((country ?? '').trim().toUpperCase());
}

/**

 * HOW AN INTERNATIONAL REQUEST IS NAMED TO A HUMAN — `IR-62`.
 *
 * A DISPLAY PREFIX, not a stored one. The row's id stays the serial integer it is, so nothing
 * about the database, the URLs, the test ids or the existing rows depends on it; only what a
 * person reads does. Storing "IR-62" would make the id a string to sort, join and search on for
 * the sake of two characters that never vary.
 *
 * Shared rather than interpolated at each site, because a queue that says "IR-62" beside a drawer
 * that says "62" reads as two different records.
 */
export const INTL_REQUEST_PREFIX = 'IR';

/** `62` → `IR-62`. */
export function intlRequestNo(id: number | string): string {
  return `${INTL_REQUEST_PREFIX}-${id}`;
}

/**
 * Read a request number a person typed, in any of the four shapes it travels in.
 *
 * `IR-62`, `ir 62`, `#62` and `62` all mean row 62 — somebody pasting from a chat message or an
 * email subject should not have to know which shape the search box wants. Returns undefined when
 * there is no number in it at all, which is how a name search stays a name search.
 *
 * The twin is `parseRequestNo` in the service; both are spec'd against the same four shapes.
 */
export function parseIntlRequestNo(input: string): number | undefined {
  const m = /^\s*(?:IR[\s-]*)?#?\s*(\d+)\s*$/i.exec(input ?? '');
  if (!m) return undefined;
  const n = Number(m[1]);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

/**
 * One international device order request, filed by staff on a customer's behalf.
 *
 * THE CUSTOMER RECORD, not a projection — the opposite of `DeviceRmaRequest`, which is a copy of
 * a HubSpot ticket that the next poll may refresh. Nothing else holds these fields.
 */
/** One package in a shipment — the tracking a customer is given. */
export interface IntlMcfPackage {
  trackingNumber?: string | null;
  carrierCode?: string | null;
  estimatedArrivalDate?: string | null;
  packageNumber?: number | null;
  /**
   * Amazon's OWN id for the shipment — the only Amazon-minted identifier in this lane.
   *
   * An MCF fulfilment order has no Amazon order number: the seller names the order and Amazon
   * addresses it by that name, so `mcfOrderId` (IR-11) and the packing-slip id are both ours.
   * This appears once Amazon has created a shipment.
   */
  amazonShipmentId?: string | null;
  fulfillmentCenterId?: string | null;
  shipmentStatus?: string | null;
}

export interface IntlRequest {
  id: number;
  /** `staff` today. The seam a customer-facing form would arrive through. */
  source: string;

  // ── who filed it ──────────────────────────────────────────────────────────────────────────
  /** Operator email, from the auth token. Never a form field. */
  submittedBy: string;
  submittedAt: string;

  // ── who it is FOR ─────────────────────────────────────────────────────────────────────────
  stripeCustomerId: string;
  contactEmail: string;
  contactName?: string | null;
  company?: string | null;
  /**
   * The customer's VAT/tax registration number as supplied. Whether VAT is CHARGED is computed
   * from ship-from × ship-to at invoicing; this is only the number.
   */
  vatNumber?: string | null;
  /** Frequently absent: these requests arrive from prospects with no OptiSigns account. */
  accountId?: string | null;
  hubspotContactId?: string | null;
  hubspotDealId?: string | null;

  // ── where it ships ────────────────────────────────────────────────────────────────────────
  /** The RECIPIENT, not the account holder — on a business order those differ. */
  shipToName?: string | null;
  shipToLine1?: string | null;
  shipToLine2?: string | null;
  shipToCity?: string | null;
  shipToRegion?: string | null;
  shipToPostalCode?: string | null;
  /** ISO 3166-1 alpha-2. The one field the update mutation refuses to change. */
  shipToCountry: string;
  shipToPhone?: string | null;
  /**
   * DERIVED server-side from street + city + phone. NOT region or postal code: plenty of real
   * international addresses have neither, and requiring them would mark complete ones incomplete.
   */
  addressComplete: boolean;
  /**
   * WHICH fields are missing, in the words the form uses. Empty when the address is complete.
   *
   * Server-derived beside `addressComplete`, so the UI renders what it is told rather than
   * re-deciding it — the same rule as `stage`.
   */
  addressMissing: string[];

  // ── what ──────────────────────────────────────────────────────────────────────────────────
  deviceModel: IntlDeviceModel;
  quantity: number;
  /**
   * ISO 4217, lower-case — the currency the DEAL was negotiated in, captured at intake.
   *
   * The raise-invoice dialog defaults to it and may override it: this is what the request was
   * filed in, while what was actually billed lives on the Stripe invoice.
   */
  currency: IntlCurrency;

  // ── corrections ───────────────────────────────────────────────────────────────────────────
  lastUpdatedBy?: string | null;
  lastUpdatedAt?: string | null;

  // ── the invoice ───────────────────────────────────────────────────────────────────────────
  /** What staff paste into Stripe. */
  stripeInvoiceId?: string | null;
  /** What the customer quotes back. NULL until the invoice is FINALIZED. */
  invoiceNumber?: string | null;
  /** A CACHE of what Stripe last said. Stripe is authoritative. */
  invoiceStatus?: string | null;
  invoiceAutoSendAt?: string | null;
  invoicePaidAt?: string | null;
  /** "Checked, still unpaid" vs "nobody has looked since Tuesday" — different facts. */
  invoiceCheckedAt?: string | null;
  invoiceRaisedBy?: string | null;
  invoiceRaisedAt?: string | null;
  /**
   * Why the automatic draft failed, when it did. Filing drafts the invoice, so a failure has
   * nowhere to be seen unless it rides the row. Cleared on a successful raise.
   */
  invoiceError?: string | null;
  /**
   * A staff-facing Stripe DASHBOARD link — where a rep opens the draft to review it. Never the
   * customer's `hosted_invoice_url`, which is null on a draft.
   */
  invoiceUrl?: string | null;

  // ── the Amazon MCF fulfilment order ────────────────────────────────────────────────────────
  //
  // There is NO Shopify order in this lane (requester, 2026-09-09): a paid invoice goes straight
  // to Amazon Multi-Channel Fulfillment. The `shopifyOrder*` fields are gone (migration 036) —
  // nothing ever wrote them.
  /**
   * The `sellerFulfillmentOrderId` WE sent — `IR-19`, or `IR-19-a2` on a retry.
   *
   * Amazon's create returns an empty body, so this is the only handle there is. `ORDER_CREATED`
   * is derived from it.
   */
  mcfOrderId?: string | null;
  /** `AMAZON_US` | `AMAZON_UK` | `AMAZON_DE`. */
  mcfMarketplace?: string | null;
  /** The AMAZON seller SKU submitted — not a Shopify SKU. */
  mcfSellerSku?: string | null;
  mcfShippingSpeed?: string | null;
  /** How many order ids this request has burned. An id is unusable forever once created. */
  mcfAttempt: number;
  mcfSubmittedAt?: string | null;
  mcfSubmittedBy?: string | null;
  /** When a person RELEASED the held order, and who. Only this means goods are moving. */
  mcfReleasedAt?: string | null;
  mcfReleasedBy?: string | null;
  mcfCancelledAt?: string | null;
  mcfCancelledBy?: string | null;
  /** A CACHE of Amazon's own word: Received | Planning | Processing | Complete | Cancelled. */
  mcfStatus?: string | null;
  /** When we last ASKED — a different fact from when Amazon last changed it. */
  mcfStatusAt?: string | null;
  /** Why a submission did not happen. Cleared on success. Explains an absence. */
  mcfError?: string | null;
  /** DERIVED: accepted by Amazon and not yet released. The "waiting for a person" flag. */
  mcfHeld: boolean;

  // ── The two ROOT-ONLY overrides ────────────────────────────────────────────────────────────
  /**
   * When root approved shipping BEFORE the invoice was paid, and why.
   *
   * Surfaced rather than buried in an audit table: a request that shipped on trust looks
   * identical to a paid one unless the row says otherwise, and that is the first question asked
   * when the money never arrives.
   */
  mcfUnpaidApprovedAt?: string | null;
  mcfUnpaidApprovedBy?: string | null;
  mcfUnpaidReason?: string | null;
  /** When root switched the customer's account off over this unpaid shipment. No undo here. */
  accountDeactivatedAt?: string | null;
  accountDeactivatedBy?: string | null;
  accountDeactivatedReason?: string | null;
  /** WHICH account was resolved at the moment of the shutoff. */
  deactivatedAccountId?: string | null;
  /**
   * Shipped from CHINA rather than Amazon MCF — the second fulfilment route (2026-09-17).
   *
   * Reaches the same FULFILLED stages an Amazon tracking number does, and takes the request off
   * the Amazon lane so the twice-daily poll cannot place a duplicate held order.
   */
  /**
   * What Amazon quoted at submit, for EVERY speed — see migration 042. The comparison it feeds
   * (Amazon's fee against the freight we billed) is why it is kept.
   */
  mcfQuote?: {
    speed: string;
    fulfillable: boolean;
    feeMinor?: number | null;
    currency?: string | null;
    earliestShip?: string | null;
    latestShip?: string | null;
    earliestArrival?: string | null;
    latestArrival?: string | null;
  }[] | null;
  mcfQuotedAt?: string | null;
  /** The CHOSEN speed's fee, in MINOR units. */
  mcfFeeMinor?: number | null;
  mcfFeeCurrency?: string | null;
  /** The freight actually BILLED, in MINOR units — the other half of the margin comparison. */
  invoiceFreightMinor?: number | null;

  /** The customer's purchase order number, written into the invoice memo. */
  poNumber?: string | null;
  /** `custops_document.id` of the uploaded PO, when the request has an account to file it under. */
  poDocumentId?: string | null;

  // ── the QUOTE, when there is one ────────────────────────────────────────────────────────────
  //
  // ⚠️ NOT `mcfQuote` below, which is what AMAZON charges US to ship. These are what WE offered
  // the CUSTOMER. Same shipment, opposite sides of the margin.

  /** The Stripe quote (`qt_…`) this request was priced from. */
  stripeQuoteId?: string | null;
  /** Stripe's own word, cached: `draft` | `open` | `accepted` | `canceled`. */
  quoteStatus?: string | null;
  /** Assigned at FINALIZATION — absent means there is no PDF to download yet. */
  quoteNumber?: string | null;
  /** Minor units, all three. */
  quoteUnitAmount?: number | null;
  quoteShippingAmount?: number | null;
  quoteTotal?: number | null;
  quoteCurrency?: string | null;
  /** Past this, converting is refused and re-quoting is the way forward. */
  quoteExpiresAt?: string | null;
  /**
   * THE STALENESS PAIR — what the quote was priced FOR, not what the request says today.
   *
   * The request stays editable while quoted, so these are how the drawer can say "was 7, is now
   * 12" instead of leaving a rep to guess which edit invalidated the price.
   */
  quoteQuantity?: number | null;
  quoteShipToCountry?: string | null;
  /** How many times the quote has been re-priced. 0 for the first. */
  quoteRevision?: number | null;
  quoteCreatedBy?: string | null;
  quoteCreatedAt?: string | null;
  quoteAcceptedBy?: string | null;
  quoteAcceptedAt?: string | null;
  quoteError?: string | null;

  /**
   * WHAT AMAZON QUOTED FOR FREIGHT when this quote was raised — not what the customer was billed.
   *
   * The quote bills the 50 + 20 tier (`quoteShippingAmount`); this is what shipping it actually
   * costs us. Distinct from `mcfFeeMinor`, which is recorded at SUBMIT, weeks later, on the
   * shipment we placed — both can exist and legitimately differ. Null is ordinary: Amazon cannot
   * always be asked. Migration 053.
   */
  quoteAmazonFeeMinor?: number | null;
  /** The MARKETPLACE's currency, which is not always the invoice's — IR-109 is USD/EUR. */
  quoteAmazonFeeCurrency?: string | null;
  quoteAmazonSpeed?: string | null;
  quoteAmazonQuotedAt?: string | null;

  chinaTrackingNumber?: string | null;
  /** Optional — only decides whether the number renders as a link to the carrier's own page. */
  chinaCarrier?: string | null;
  chinaFulfilledAt?: string | null;
  chinaFulfilledBy?: string | null;
  /** Tracking. An ARRAY — Amazon splits an order across shipments and packages. */
  mcfPackages: IntlMcfPackage[];

  /** DERIVED from the stamps above, never stored. */
  stage: IntlStage;
}

/**
 * What a request would cost, priced but not committed — the intake form's summary.
 *
 * MINOR UNITS throughout, from the server's own `priceIntlRequest`. There is deliberately no
 * client-side pricing engine: a second set of numbers is a second thing to disagree with the
 * document the customer ends up holding.
 */
/**
 * What a request comes to — the summary shown before anything is raised.
 *
 * ⚠️ `shippingAmount` and `total` ARE NULLABLE, and that is the shape of the 2026-09-22 change.
 * Freight is what Amazon charges to fulfil this exact shipment, read live, and Amazon cannot
 * always be asked — stock out at this quantity, a half-typed address, an SP-API outage, a
 * marketplace that prices in another currency, or any non-production environment. There are no
 * freight tiers left to fall back on, by design. Null means "unknown", NEVER zero: zero would
 * quote free shipping. `freightUnavailableReason` says which it is.
 */
export interface IntlRequestPricing {
  unitAmount: number;
  shippingAmount?: number | null;
  freightUnavailableReason?: string | null;
  freightCostMinor?: number | null;
  freightSpeed?: string | null;
  /** How the fee was converted into this currency, when it had to be. Null on the normal path. */
  freightFxNote?: string | null;
  total?: number | null;
  currency: string;
  /** The catalog row the unit price came from, so a figure can be traced. */
  sku: string;
  quantity: number;
}

/** One shipping speed as Amazon prices it right now. `feeMinor` absent when it refused the speed. */
export interface IntlFreightSpeedQuote {
  speed: string;
  /** False is a RESULT — usually `InventoryUnavailable` at this quantity — not an error. */
  fulfillable: boolean;
  feeMinor?: number | null;
  currency?: string | null;
  latestArrival?: string | null;
}

/**
 * What Amazon would charge to ship a request, for display beside `IntlRequestPricing`.
 *
 * ⚠️ `speeds: []` WITH an `unavailableReason` IS THE SUCCESS SHAPE. Amazon cannot be asked for
 * many ordinary reasons and none of them stops a rep quoting — the reason is a sentence to show.
 * What WE bill is deliberately absent: `IntlRequestPricing.shippingAmount` owns that figure.
 */
export interface IntlFreightQuote {
  speeds: IntlFreightSpeedQuote[];
  unavailableReason?: string | null;
  marketplace?: string | null;
  sellerSku?: string | null;
}

/**
 * Raise, or re-price, the quote. The same shape as `RaiseIntlInvoiceInput` plus an expiry.
 *
 * `supersedeReason` is REQUIRED when superseding and ignored otherwise — it is the only record
 * of why the price changed, and it lands in the request's notes.
 */
export interface CreateIntlQuoteInput {
  requestId: number;
  /** Minor units, per unit. Omit to price from the catalog. */
  unitAmount?: number;
  /** Minor units, total freight. Omit or 0 when it is inside the unit price. */
  shippingAmount?: number;
  currency?: string;
  /** Defaults to 30. A quote expires; an invoice does not. */
  expiresInDays?: number;
  supersedeReason?: string;
}

export interface IntlRequestPage {
  items: IntlRequest[];
  total: number;
}

export interface IntlRequestNote {
  id: number;
  staffEmail: string;
  text: string;
  createdAt: string;
}

/**
 * What the New Request form sends. No submitter and no source: the server takes the first from
 * the authenticated caller and sets the second itself.
 */
export interface SubmitIntlRequestInput {
  stripeCustomerId: string;
  contactEmail: string;
  contactName?: string;
  company?: string;
  vatNumber?: string;
  accountId?: string;
  hubspotContactId?: string;
  hubspotDealId?: string;
  /** ISO 3166-1 alpha-2. The server refuses anything else by name. */
  shipToCountry: string;
  /**
   * The destination. ALL OPTIONAL on purpose: a rep filing from a call frequently does not have
   * it, and refusing a 600-unit request over a missing postcode would lose the demand signal.
   */
  shipToName?: string;
  shipToLine1?: string;
  shipToLine2?: string;
  shipToCity?: string;
  shipToRegion?: string;
  shipToPostalCode?: string;
  /**
   * REQUIRED at filing — no international courier can book a delivery without one. Typed
   * optional only so the server's own named refusal is what a rep sees.
   */
  shipToPhone?: string;
  deviceModel: IntlDeviceModel;
  quantity: number;
  /** ISO 4217 — the deal currency. Optional; the server defaults it to `usd`. */
  currency?: IntlCurrency;
}

/**
 * What fulfilment (or the filer) may correct after filing.
 *
 * NO `shipToCountry` and NO `deviceModel`, deliberately — together they are the compliance and
 * sourcing identity of the request. A wrong country is a different request, not a typo.
 *
 * Every other field is optional and omitting one LEAVES IT ALONE. There is no way to clear a
 * field back to empty: GraphQL cannot distinguish absent from explicit null here without a
 * wrapper type, and silently blanking a customer's address because a client sent null is worse
 * than not supporting clearing.
 */
export interface UpdateIntlRequestInput {
  requestId: number;
  stripeCustomerId?: string;
  contactEmail?: string;
  contactName?: string;
  company?: string;
  vatNumber?: string;
  accountId?: string;
  hubspotContactId?: string;
  hubspotDealId?: string;
  shipToName?: string;
  shipToLine1?: string;
  shipToLine2?: string;
  shipToCity?: string;
  shipToRegion?: string;
  shipToPostalCode?: string;
  shipToPhone?: string;
  quantity?: number;
  /** Correctable — a deal can be renegotiated into another currency before invoicing. */
  currency?: IntlCurrency;
}

/** The currencies the hardware catalog is priced in — what the invoice may be raised in. */
/**
 * The currencies an international request may be billed in.
 *
 * NARROWED to three on 2026-09-21 (requester): "Only need USD, GBP and EUR". It carried `cad` and
 * `aud` before, inherited from the hardware catalog's wider list rather than from anything this
 * lane ever sold in — no request has ever been filed in either (checked: 24 usd, 8 eur, nothing
 * else).
 *
 * ⚠️ MIGRATION 032's CHECK IS DELIBERATELY WIDER and still admits all five. The app is the
 * narrower gate on purpose: a CHECK is the wrong place to express a commercial decision that may
 * be reversed next quarter, and tightening it would mean a migration that could refuse a row the
 * database already holds. If this list ever widens again, nothing in the database has to move.
 */
export const INTL_CURRENCIES = ['usd', 'gbp', 'eur'] as const;
export type IntlCurrency = (typeof INTL_CURRENCIES)[number];

/** The symbol shown beside each code, so a rep sees which money they are billing in. */
export const INTL_CURRENCY_SYMBOLS: Readonly<Record<IntlCurrency, string>> = {
  usd: '$',
  gbp: '£',
  eur: '€',
};

/**
 * ── WHICH CURRENCY A DESTINATION SHOULD BE BILLED IN ──────────────────────────────────────────
 *
 * The currency of the Amazon marketplace that will fulfil it: US → USD, GB → GBP, and the 27
 * Amazon-DE destinations → EUR.
 *
 * ⚠️ THIS IS NOT A PREFERENCE, IT DECIDES WHETHER THE REQUEST CAN BE BILLED AT ALL. Since
 * 2026-09-22 freight is Amazon's measured fee, and Amazon answers in the MARKETPLACE's currency.
 * There is no FX anywhere in this lane and Stripe refuses a quote whose line currency differs
 * from the customer's, so a USD request shipping from Amazon DE has no freight figure and cannot
 * be quoted or invoiced without somebody typing one. Production already holds one: IR-109, USD
 * into Germany, filed before any of this was visible.
 *
 * ⚠️ AND IT MIRRORS `MCF_MARKETPLACE_CANDIDATES`, which lives server-side in `mcf-routing.ts` and
 * is the authority. `mcf-routing.spec.ts` asserts the two agree for every servable country, so
 * an edit to one that is not made to the other fails CI rather than silently proposing a
 * currency that cannot be billed.
 *
 * Returns null for anything not servable — the picker only offers `MCF_SERVABLE_COUNTRIES`, so
 * that is a defensive branch rather than a reachable one.
 */
export function intlCurrencyForDestination(
  shipToCountry: string | null | undefined,
): IntlCurrency | null {
  const cc = (shipToCountry ?? '').trim().toUpperCase();
  if (!cc || !MCF_SERVABLE_COUNTRIES.includes(cc)) return null;
  if (cc === 'US') return 'usd';
  if (cc === 'GB') return 'gbp';
  return 'eur';
}

/**
 * Raise the draft invoice for a request.
 *
 * NO PRODUCT AND NO QUANTITY: both come from the request, so an invoice cannot be raised for
 * hardware the customer did not ask for, or for a different number of units.
 *
 * THE OPERATOR SUPPLIES THE PRICE. A bulk international order is negotiated — freight, duty
 * exposure and the sourcing choice all move the number — so a catalog lookup would bill a figure
 * nobody agreed to.
 */
export interface RaiseIntlInvoiceInput {
  requestId: number;
  /**
   * MINOR UNITS, per unit — 79900 is $799.00. OMIT to price from the Mongo catalog, which is
   * what filing does and what the manual retry does unless somebody deliberately overrides it.
   */
  unitAmount?: number;
  /** MINOR UNITS, total freight. Omitted alongside `unitAmount` when pricing from the catalog. */
  shippingAmount?: number;
  /** ISO 4217. Defaults to the currency the request was filed in. */
  currency?: IntlCurrency;
  /**
   * The customer's purchase order number, written into the invoice MEMO after the intra-Community
   * VAT statement where one is due. Free text: a PO number is the buyer's format, not ours.
   */
  poNumber?: string;
  /**
   * `custops_document.id` of the PO uploaded to the customer's Document Center.
   *
   * ⚠️ Only possible when the request HAS an account — `accountId` is frequently null here, and
   * the server refuses an id on a request that has none rather than filing it somewhere arbitrary.
   */
  poDocumentId?: string;
}

/**
 * The result of re-reading every live invoice from Stripe.
 *
 * COUNTS, not just rows: after pressing Refresh, "checked but nothing moved" and "nothing to
 * check" look identical without them, and which one happened is the whole feedback.
 */
export interface IntlInvoiceRefresh {
  checked: number;
  changed: number;
  failed: number;
}

/**
 * Minor units → a display string, using the currency's own conventions.
 *
 * MINOR UNITS ARE THE WIRE FORMAT throughout this feature, so every display site needs this and
 * none of them should hand-roll `/100`: not every currency has two decimal places, and a
 * hard-coded divisor is how a JPY amount gets shown 100x too small.
 */
export function formatMinor(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  } catch {
    return `${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

/**
 * The customer's BILLING address as Stripe holds it, in Stripe's own field names.
 *
 * `state` is Stripe's name for what the form calls "Region / state" and what the request row
 * stores as `shipToRegion`. Three names, one fact — the translation lives server-side in
 * `stripe-address.ts`, and this type is the Stripe end of it.
 */
export interface IntlStripeAddressInfo {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  /** ISO 3166-1 alpha-2. */
  country?: string | null;
}

/**
 * A Stripe customer, resolved so the intake form can prefill itself.
 *
 * EVERY FIELD BUT `id` AND `deleted` IS OPTIONAL — a Stripe customer is genuinely allowed to have
 * no name, no email, no phone and no tax id. A missing field means "nothing to prefill".
 *
 * ⚠️ `billingAddress` IS NOT A PREFILL SOURCE. It used not to be carried at all, and the reason
 * still stands: Stripe holds the BILLING address and the form's block is where the devices SHIP,
 * which on an international order are routinely different. It is here for one thing — filing a
 * request OVERWRITES `customer.address` with the destination (requester, 2026-09-18), so the form
 * shows the rep what is about to be replaced. Seeding a ship-to field from it would reintroduce
 * exactly the hazard its absence was protecting against.
 */
export interface IntlStripeCustomerInfo {
  id: string;
  /** True when the customer has been DELETED in Stripe — a different customer is the fix. */
  deleted: boolean;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  /** `shipping.name` — who a courier asks for, not the same question as `name`. */
  contactName?: string | null;
  vatNumber?: string | null;
  /** What filing replaces. Null when Stripe holds no address — nothing to warn about. */
  billingAddress?: IntlStripeAddressInfo | null;
  /**
   * The currency Stripe has LOCKED this customer to, if any.
   *
   * Set on their first invoice or subscription and immutable afterwards, so every later quote and
   * invoice must match it. Absent means never billed — the first document decides it.
   */
  currency?: string | null;
}

export interface IntlRequestFilterInput {
  /** Spans the request number, company, contact, Stripe customer id and Stripe invoice id. */
  search?: string;
  shipToCountry?: string;
  deviceModel?: IntlDeviceModel;
  stage?: IntlStage;
  /** Stripe's word, or `none` for a row with no invoice. A second axis, not an alternative. */
  invoiceStatus?: StripeInvoiceStatus | 'none';
  /**
   * Only the caller's own filings. A boolean rather than a submitter string, so nobody with the
   * tab can enumerate a colleague's pipeline.
   */
  mine?: boolean;
}
